import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcFileAlt, IcPdf, IcSearch } from "../../Icons";
import axios from "axios";
import { Button, Table, Input, Label } from "reactstrap";
import Header from "../../layout/Header/Header";
import moment from "moment";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { pdfHeader, pdfFooter, pdfTableStyles } from "../../../pdfUtils";

const fmt = (v) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(v) || 0);

function EstadoCuenta() {
  const { user } = useContext(AuthContext);
  const URL_ESTADO_CUENTA = process.env.REACT_APP_URL_ESTADO_CUENTA;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
  const auth = { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } };

  // Permite llegar desde el listado de clientes con ?cliente=<id>
  const clienteInicial = new URLSearchParams(window.location.search).get("cliente") || "";

  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(clienteInicial);
  const [fechaInicio, setFechaInicio] = useState(moment().startOf("year").format("YYYY-MM-DD"));
  const [fechaFin, setFechaFin] = useState(moment().format("YYYY-MM-DD"));
  const [usaFechas, setUsaFechas] = useState(false);

  const [cliente, setCliente] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [saldoInicial, setSaldoInicial] = useState(0);

  useEffect(() => {
    axios.get(URL_CLIENTES, auth).then((r) => setClientes(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const consultar = () => {
    if (!selectedCliente) return;

    const peticion = usaFechas
      ? axios.post(`${URL_ESTADO_CUENTA}/${selectedCliente}/fechas`,
          { selectedFechaInicio: fechaInicio, selectedFechaFin: fechaFin }, auth)
      : axios.get(`${URL_ESTADO_CUENTA}/${selectedCliente}`, auth);

    peticion
      .then((response) => {
        setCliente(response.data.cliente);
        setMovimientos(response.data.movimientos);
        setSaldoInicial(response.data.saldoInicial || 0);
      })
      .catch(() => {});
  };

  useEffect(() => { consultar(); }, [selectedCliente]); // eslint-disable-line

  const totalEntradas = movimientos
    .filter((m) => m.movimiento === "Entrada")
    .reduce((s, m) => s + m.monto, 0);
  const totalComisiones = movimientos.reduce((s, m) => s + m.comisionTotal, 0);
  const totalSalidas = movimientos
    .filter((m) => m.movimiento === "Salida")
    .reduce((s, m) => s + m.monto, 0);
  const saldoFinal = movimientos.length ? movimientos[movimientos.length - 1].saldo : saldoInicial;

  function generaPDF() {
    const doc = new jsPDF();
    const subtitulo = usaFechas
      ? `${cliente?.nombre_comercial || ""} — ${fechaInicio} al ${fechaFin}`
      : `${cliente?.nombre_comercial || ""}`;
    const startY = pdfHeader(doc, "Estado de Cuenta", subtitulo);

    doc.autoTable({
      startY,
      head: [["Movimiento", "Tipo", "Fecha", "Monto", "Comisión Total", "Restante", "Saldo"]],
      body: movimientos.map((m) => [
        m.movimiento,
        m.tipo,
        m.fecha,
        fmt(m.monto),
        fmt(m.comisionTotal),
        fmt(m.restante),
        fmt(m.saldo),
      ]),
      foot: [["", "", "", fmt(totalEntradas), fmt(totalComisiones), fmt(totalSalidas), fmt(saldoFinal)]],
      ...pdfTableStyles(),
    });

    pdfFooter(doc);
    doc.save(`EstadoCuenta_${cliente?.nombre_comercial || "cliente"}.pdf`);
  }

  return (
    <>
      <Header />
      {user.menu_estadoCuenta ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <IcFileAlt style={{ marginRight: 8, color: "#3b82f6" }} />
                Estado de Cuenta
              </h3>
              <p className="page-subtitle">Movimientos y saldo por cliente</p>
            </div>
            <div className="page-actions">
              <Button color="danger" size="sm" onClick={generaPDF} disabled={!cliente}>
                <IcPdf style={{ marginRight: 6 }} />
                PDF
              </Button>
            </div>
          </div>

          <div className="filter-bar">
            <div style={{ minWidth: 220 }}>
              <Label>Cliente</Label>
              <Input bsSize="sm" type="select" value={selectedCliente}
                     onChange={(e) => setSelectedCliente(e.target.value)}>
                <option value="">Selecciona</option>
                {clientes
                  .filter((a) => a.is_active === "Si")
                  .sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1))
                  .map((a) => <option key={a._id} value={a._id}>{a.nombre_comercial}</option>)}
              </Input>
            </div>
            <div>
              <Label>Acotar fechas</Label>
              <Input bsSize="sm" type="select" value={usaFechas ? "Si" : "No"}
                     onChange={(e) => setUsaFechas(e.target.value === "Si")}>
                <option value="No">No</option>
                <option value="Si">Sí</option>
              </Input>
            </div>
            <div>
              <Label>Fecha inicio</Label>
              <Input bsSize="sm" type="date" value={fechaInicio} disabled={!usaFechas}
                     onChange={(e) => setFechaInicio(e.target.value)} />
            </div>
            <div>
              <Label>Fecha fin</Label>
              <Input bsSize="sm" type="date" value={fechaFin} disabled={!usaFechas}
                     onChange={(e) => setFechaFin(e.target.value)} />
            </div>
            <div>
              <Button color="primary" size="sm" onClick={consultar} disabled={!selectedCliente}>
                <IcSearch style={{ marginRight: 6 }} />
                Consultar
              </Button>
            </div>
          </div>

          {cliente ? (
            <>
              <div className="stats-row">
                <div className="stat-chip">Entradas: <strong>{fmt(totalEntradas)}</strong></div>
                <div className="stat-chip">Comisiones: <strong>{fmt(totalComisiones)}</strong></div>
                <div className="stat-chip">Salidas: <strong>{fmt(totalSalidas)}</strong></div>
                <div className="stat-chip">Saldo: <strong>{fmt(saldoFinal)}</strong></div>
              </div>

              <Table size="sm" striped borderless className="table-responsive-xl table-dark-header">
                <thead>
                  <tr>
                    <th>Movimiento</th>
                    <th>Tipo</th>
                    <th>Fecha</th>
                    <th style={{ textAlign: "right" }}>Monto</th>
                    <th style={{ textAlign: "right" }}>Comisión Total</th>
                    <th style={{ textAlign: "right" }}>Restante</th>
                    <th style={{ textAlign: "right" }}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {usaFechas ? (
                    <tr>
                      <td colSpan={6} style={{ fontStyle: "italic", color: "#64748b" }}>
                        Saldo inicial al {fechaInicio}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(saldoInicial)}</td>
                    </tr>
                  ) : null}
                  {movimientos.map((m) => (
                    <tr key={`${m.movimiento}-${m._id}`}>
                      <td>
                        <span style={{
                          fontWeight: 600,
                          color: m.movimiento === "Entrada" ? "#059669" : "#dc2626",
                        }}>
                          {m.movimiento}
                        </span>
                      </td>
                      <td>{m.tipo}</td>
                      <td>{m.fecha}</td>
                      <td style={{ textAlign: "right" }}>{fmt(m.monto)}</td>
                      <td style={{ textAlign: "right" }}>{fmt(m.comisionTotal)}</td>
                      <td style={{ textAlign: "right" }}>{fmt(m.restante)}</td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(m.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} style={{ textAlign: "right", fontWeight: 700 }}>Totales</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(totalEntradas)}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(totalComisiones)}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(totalSalidas)}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(saldoFinal)}</td>
                  </tr>
                </tfoot>
              </Table>
            </>
          ) : (
            <p style={{ color: "#94a3b8", fontSize: "0.82rem" }}>
              Selecciona un cliente para ver su estado de cuenta.
            </p>
          )}
        </div>
      ) : undefined}
    </>
  );
}

export default EstadoCuenta;
